/**
 * NavigationSidebarV2.tsx - V2 sidebar content with SelectrNavigation
 * 
 * This component provides the sidebar content for V2 navigation.
 * It uses SelectrNavigation instead of the classic SideMenu.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { SelectrNavigation } from '../../../../packages/selectr/src/SelectrNavigation';
import type { SelectrItem } from '@opcentral/dependr';
import { usePanelr } from '@opcentral/dependr';
import { pluginRegistry } from '../pluginRegistry';
import { useSkinConfig } from '../hooks/useSkinConfig';
import type { ToolType } from '../types';

interface NavigationSidebarV2Props {
  currentTool: ToolType;
  onToolChange: (tool: ToolType) => void;
}

// Category icons and labels
const CATEGORY_CONFIG: Record<string, { label: string; icon: string }> = {
  document: { label: 'Documents', icon: '📄' },
  data: { label: 'Data Processing', icon: '🔄' },
  ui: { label: 'UI Components', icon: '📊' },
  integration: { label: 'FileMaker Integration', icon: '🔗' },
  utility: { label: 'Utilities', icon: '🛠️' },
  examples: { label: 'Examples & Demos', icon: '✨' },
  'filemaker-scripts': { label: 'FileMaker Scripts', icon: '📜' },
  system: { label: 'System', icon: '⚙️' }
};

export const NavigationSidebarV2: React.FC<NavigationSidebarV2Props> = ({
  currentTool,
  onToolChange
}) => {
  const { getState } = usePanelr();
  const { getConfig } = useSkinConfig();
  
  // Get navigation config from skin
  const navConfig = useMemo(() => getConfig('navigation'), [getConfig]);
  
  // Get sidebar state
  const [sidebarMode, setSidebarMode] = useState(() => getState('fmwvtool-sidebar')?.mode || 'normal');
  
  // Favorites state
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('fmwvtool-favorites');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Persist favorites
  useEffect(() => {
    localStorage.setItem('fmwvtool-favorites', JSON.stringify(Array.from(favorites)));
  }, [favorites]);

  // Convert plugin registry to SelectrItems
  const selectrItems = useMemo((): SelectrItem[] => {
    const items: SelectrItem[] = [];
    
    // Filter plugins that should show in nav (enabled and showInNav !== false)
    const navPlugins = pluginRegistry.filter(p => p.enabled && p.showInNav !== false);
    
    // Group plugins by category
    const byCategory = navPlugins.reduce((acc, plugin) => {
      const category = plugin.category || 'utility';
      if (!acc[category]) acc[category] = [];
      acc[category].push(plugin);
      return acc;
    }, {} as Record<string, typeof navPlugins>);

    // Convert to SelectrItems with groups
    Object.entries(byCategory).forEach(([category, plugins]) => {
      const config = CATEGORY_CONFIG[category] || { label: category, icon: '📦' };
      
      plugins.forEach(plugin => {
        items.push({
          id: plugin.id,
          label: plugin.name,
          value: plugin.id,
          icon: getPluginIcon(plugin),
          description: plugin.description,
          group: config.label,
          isFavorite: favorites.has(plugin.id),
          metadata: { category }
        });
      });
    });

    return items;
  }, [favorites]);

  const handleItemClick = (item: SelectrItem) => {
    console.log('[NavigationSidebarV2] Item clicked:', item.id);
    onToolChange(item.id as ToolType);
  };

  const handleFavoriteToggle = (item: SelectrItem, isFavorite: boolean) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFavorite) {
        next.add(item.id as string);
      } else {
        next.delete(item.id as string);
      }
      return next;
    });
  };

  const isMinimized = sidebarMode === 'minimized';

  return (
    <SelectrNavigation
      items={selectrItems}
      currentItemId={currentTool}
      onItemClick={handleItemClick}
      onFavoriteToggle={handleFavoriteToggle}
      searchable={true}
      showFavorites={true}
      showDisclosureTriangles={true}
      grouped={true}
      className={isMinimized ? 'minimized' : ''}
    />
  );
};

// Helper to get appropriate icon for plugin
function getPluginIcon(plugin: typeof pluginRegistry[0]): string {
  // Use tag-specific icons
  if (plugin.tags.includes('pdf')) return '📕';
  if (plugin.tags.includes('office')) return '📊';
  if (plugin.tags.includes('table')) return '📋';
  if (plugin.tags.includes('tags')) return '🏷️';
  if (plugin.tags.includes('filemaker')) return '🔗';
  if (plugin.tags.includes('data')) return '🔄';
  if (plugin.tags.includes('picker')) return '🎯';
  if (plugin.tags.includes('files')) return '📁';
  if (plugin.tags.includes('memory')) return '💾';
  if (plugin.tags.includes('scripts')) return '📜';
  
  // Default by category
  const categoryIcons: Record<string, string> = {
    document: '📄',
    data: '🔄',
    ui: '📊',
    integration: '🔗',
    utility: '🛠️',
    examples: '✨',
    'filemaker-scripts': '📜'
  };
  
  return categoryIcons[plugin.category] || '📦';
}
